import React, { useEffect, useMemo, useRef, useState,useCallback } from "react";
import axiosApi from "../Utils/axiosApi";
import HeaderMenu from "../Header/HeaderMenu";
import {
  Alert, Box, Button, CircularProgress, Fade, FormControl, Grid,
  Grow, IconButton, InputLabel, MenuItem, Paper, Select, Stack,
  Tooltip, Typography, useMediaQuery, useTheme, Dialog, DialogTitle,
  DialogContent, 
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import GanttChart from "./GanttChart";
import TenantDialog from "./TenantDialog";
import CreateHosteler from "../HostelDetails/CreateHosteler";

//const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Parse the calendar date portion of an API ISO date without allowing the
 * browser timezone to shift a date-only booking to the previous/next day.
 */
const parseApiDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? null
    : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date, amount) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);

const addMonths = (date, amount) =>
  new Date(date.getFullYear(), date.getMonth() + amount, 1);

const isFoodOnly = (tenant, roomTypeName) => {
  // Trust the explicit accommodation type when supplied by the API.
  if (tenant?.accommodationType) return tenant.accommodationType === "FOOD_ONLY";
  return !roomTypeName;
};

const flattenGantt = (ganttData, selectedHostelId = "") => {
  if (!Array.isArray(ganttData) || !ganttData.length) return [];
  const rows = [];

  ganttData.forEach((hostel, hostelIndex) => {
    (hostel?.roomTypeGanttDTOs || []).forEach((roomType, typeIndex) => {
      const roomTypeName = String(roomType?.roomType || "").trim();
      const capacity = roomType?.totalCapacity ?? 0;

      (roomType?.roomGanttDTOs || []).forEach((room, roomIndex) => {
        const roomNumber = String(room?.roomNumber ?? "").trim() || String(roomIndex + 1);

        (room?.tenantGanttDTOs || []).forEach((tenant, tenantIndex) => {
          const startDate = parseApiDate(tenant?.startDate);
          if (!startDate) return;

          const foodOnly = isFoodOnly(tenant, roomTypeName);
          const group = foodOnly ? "food" : "room";
          const location = foodOnly
            ? `Food Only - ${tenant?.tenantName || "Tenant"}`
            : `${roomTypeName} - Room ${roomNumber}`;
rows.push({
  ...tenant,
  uiKey: `${hostelIndex}-${typeIndex}-${roomIndex}-${tenantIndex}`,
  hostelName: hostel?.hostelName || "",
  hostelId: hostel?.hostelId ?? hostel?.id ?? selectedHostelId,
  roomId: room?.id ?? room?.roomId ?? null,
  roomStatus: room?.roomStatus ?? room?.status ?? "",
  maintenanceStatus: room?.maintenanceStatus ?? "",
  cleaningStatus: room?.cleaningStatus ?? "",
  isUnderMaintenance:
    room?.isUnderMaintenance ?? room?.underMaintenance ?? false,
  needsCleaning:
    room?.needsCleaning ?? room?.isCleaningRequired ?? false,
  roomType: roomTypeName,
  roomCapacity: capacity,
  roomNumber: foodOnly ? "" : roomNumber,
  yLabel: `${location}__${hostelIndex}_${typeIndex}_${roomIndex}_${tenantIndex}`,
  tenantName: tenant?.tenantName || "Tenant",
  mobileNumber: tenant?.mobileNumber || "",
  accommodationType:
    tenant?.accommodationType || (foodOnly ? "FOOD_ONLY" : ""),
  startDate,
  endDate: parseApiDate(tenant?.endDate),

  // Monthly invoice and payment information
  monthlyFeeStatusDTOs: Array.isArray(tenant?.monthlyFeeStatusDTOs)
    ? tenant.monthlyFeeStatusDTOs
    : [],

  // Preserve hosteler identifiers
  id: tenant?.id ?? null,
  hostelerId: tenant?.hostelerId ?? tenant?.hostelerID ?? null,
  tenantId: tenant?.tenantId ?? null,
  activeHosteler: tenant?.activeHosteler ?? null,

  group,
});
        });
      });
    });
  });

  return rows;
};

const StatCard = ({ icon, title, value, color }) => (
  <Paper
    sx={{
      p: 1.5, borderRadius: 2.5, height: "100%", minHeight: 76,
      border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15,23,42,.04)",
      transition: "box-shadow .18s, border-color .18s",
      "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 12px rgba(15,23,42,.07)" },
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{
        width: 38, height: 38, borderRadius: 2, display: "flex", flexShrink: 0,
        alignItems: "center", justifyContent: "center", background: color, color: "#fff",
        "& svg": { fontSize: 20 },
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 22, lineHeight: 1.15, fontWeight: 750, color: "#0f172a" }}>{value}</Typography>
        <Typography sx={{ fontSize: 11.5, mt: 0.35, color: "#64748b", whiteSpace: "nowrap" }}>{title}</Typography>
      </Box>
    </Stack>
  </Paper>
);

const getVisibleRange = (view, anchor) => {
  const day = startOfDay(anchor);
  if (view === "daily") {
    // Two-week window, with the anchor day near the middle. Navigation advances one day.
    return { minDate: addDays(day, -6), maxDate: addDays(day, 8), tickUnit: "day" };
  }
  if (view === "weekly") {
    const mondayOffset = (day.getDay() + 6) % 7;
    const minDate = addDays(day, -mondayOffset);
    return { minDate, maxDate: addDays(minDate, 56), tickUnit: "day" };
  }
  const minDate = new Date(day.getFullYear(), day.getMonth(), 1);
  // Every view stays within a six-month maximum timeline.
  return { minDate, maxDate: addMonths(minDate, 6), tickUnit: "month" };
};

const formatRange = (view, minDate, maxDate) => {
  const fmt = (date, options) => date.toLocaleDateString(undefined, options);
  const lastVisibleDay = addDays(maxDate, -1);
  if (view === "monthly") {
    return `${fmt(minDate, { month: "short", year: "numeric" })} – ${fmt(lastVisibleDay, { month: "short", year: "numeric" })}`;
  }
  return `${fmt(minDate, { day: "numeric", month: "short", year: "numeric" })} – ${fmt(lastVisibleDay, { day: "numeric", month: "short", year: "numeric" })}`;
};

const HostelerGantt = ({ onAddHosteler }) => {
  const chartContainerRef = useRef(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  
   // Create/Edit Hosteler dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [selectedHosteler, setSelectedHosteler] = useState(null);

  const handleCloseDialog = () => {
  setIsDialogOpen(false);
  setSelectedHosteler(null);
  setIsUpdate(false);

  // Refresh Gantt after closing the Create/Edit dialog
  if (selectedHostelId) {
    loadOccupancy(selectedHostelId, minDate, maxDate);
  }
};
  
  
  
  const [roomOverrides, setRoomOverrides] = useState({});
  const [hostelList, setHostelList] = useState([]);
  const [selectedHostelId, setSelectedHostelId] = useState("");
  const [ganttData, setGanttData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [view, setView] = useState("daily");
  const [anchorDate, setAnchorDate] = useState(() => startOfDay(new Date()));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const range = useMemo(() => getVisibleRange(view, anchorDate), [view, anchorDate]);
  const { minDate, maxDate, tickUnit } = range;

  const flattened = useMemo(() => flattenGantt(ganttData, selectedHostelId).map((tenant) => {
    const override = roomOverrides[tenant.uiKey];
    if (!override) return tenant;
    const roomType = override.roomType || "Room";
    return {
      ...tenant,
      roomType,
      roomNumber: override.roomNumber,
      yLabel: `${roomType} - Room ${override.roomNumber}__${tenant.uiKey}`,
    };
  }), [ganttData, roomOverrides, selectedHostelId]);
  const visibleTenants = useMemo(() => flattened
    .filter((tenant) => tenant.startDate < maxDate && (!tenant.endDate || tenant.endDate >= minDate))
    // Keep accommodation rows grouped in an operationally useful order:
    // stay bookings first, food-only records at the bottom.
    .sort((a, b) => {
      const aFood = a.accommodationType === "FOOD_ONLY" || (!a.accommodationType && a.group === "food");
      const bFood = b.accommodationType === "FOOD_ONLY" || (!b.accommodationType && b.group === "food");
      if (aFood !== bFood) return aFood ? 1 : -1;
      if (!aFood) {
        const typeOrder = String(a.roomType || "").localeCompare(String(b.roomType || ""));
        if (typeOrder) return typeOrder;
        const roomOrder = String(a.roomNumber || "").localeCompare(String(b.roomNumber || ""), undefined, { numeric: true });
        if (roomOrder) return roomOrder;
      }
      return a.startDate - b.startDate;
    }), [flattened, minDate, maxDate]);

  const today = startOfDay(new Date());
  const activeCount = visibleTenants.filter(
  (item) => String(item.activeHosteler || "").toUpperCase() === "Y"
).length;
  const upcomingCount = visibleTenants.filter((item) => item.startDate > today).length;
  const foodCount = visibleTenants.filter((item) => item.group === "food").length;
  const chartHeight = Math.max(280, visibleTenants.length * (isMobile ? 58 : 54));

  const handleFullscreen = () => {
    const target = chartContainerRef.current;
    if (target?.requestFullscreen) target.requestFullscreen();
  };

  

  useEffect(() => {
    let mounted = true;
    axiosApi.post("/hostel/getHostelName", {})
      .then((res) => {
        if (!mounted) return;
        if (Array.isArray(res.data)) {
          setHostelList(res.data.map((item, index) => ({
            id: item?.[0] ?? `hostel-${index}`,
            hostelName: item?.[1] ?? "Unnamed property",
          })));
        } else {
          setHostelList([]);
        }
      })
      .catch(() => {
        if (mounted) setErrorMsg("Failed to load hostel list.");
      });
    return () => { mounted = false; };
  }, []);

 const formatApiDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const loadOccupancy = useCallback(
  (
    hostelId = selectedHostelId,
    fromDate = minDate,
    toDate = maxDate
  ) => {
    if (!hostelId) {
      setErrorMsg("Please select a property first.");
      return;
    }

    setLoading(true);
    setErrorMsg("");
    setSelectedTenant(null);
    setRoomOverrides({});

    const request = {
      hostelIds: [Number(hostelId)],
      searchCheckInDate: formatApiDate(fromDate),
      searchCheckOutDate: formatApiDate(addDays(toDate, -1)),
    };

    axiosApi
      .post("/hosteler/getHostelOccupancyGantt", request)
      .then((res) => {
        setGanttData(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        setGanttData([]);
        setErrorMsg("Failed to load occupancy data. Please try again.");
      })
      .finally(() => setLoading(false));
  },
  [selectedHostelId, minDate, maxDate]
);

  
useEffect(() => {
  if (!selectedHostelId) return;

  loadOccupancy(selectedHostelId, minDate, maxDate);
}, [selectedHostelId, minDate, maxDate, loadOccupancy]);

  

  const navigateRange = (direction) => {
    setAnchorDate((previous) => {
      if (view === "daily") return addDays(previous, direction);
      if (view === "weekly") return addDays(previous, 7 * direction);
      return addMonths(previous, direction);
    });
  };

  return (
    <>
      <HeaderMenu />
      <Box sx={{ px: { xs: 1.25, sm: 2 }, py: { xs: 1.25, sm: 2 }, background: "#f8fafc", minHeight: "100vh" }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={0.5} sx={{ mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 750, lineHeight: 1.3, color: "#0f172a" }}>
              Hostel Planner
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>
              Room availability, guest stays and fee status
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 11, color: "#64748b", display: { xs: "none", sm: "block" } }}>
              Occupancy timeline
            </Typography>
           <Button
  size="small"
  variant="contained"
  startIcon={<PersonAddAlt1Icon />}
  disabled={!selectedHostelId}
  onClick={() => {
    setSelectedHosteler({
      hostelId: selectedHostelId,
      hostelName:
        hostelList.find(
          (item) => String(item.id) === String(selectedHostelId)
        )?.hostelName || "",
      accommodationType: "STAY_ONLY",
    });
    setIsUpdate(false);
    setIsDialogOpen(true);
  }}
  sx={{
    textTransform: "none",
    borderRadius: 1.75,
    boxShadow: "none",
    fontWeight: 700,
  }}
>
  Add new hosteler
</Button>
          </Stack>
        </Stack>

        <Paper sx={{ p: { xs: 1.25, sm: 1.75 }, mb: 1.5, borderRadius: 2.5, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15,23,42,.04)" }}>
          <Grid container spacing={1.25} alignItems="center">
            <Grid item xs={12} md={5}>
              <FormControl fullWidth size="small">
                <InputLabel id="hostel-property-label">Property</InputLabel>
                <Select
  labelId="hostel-property-label"
  value={selectedHostelId}
  label="Property"
  onChange={(event) => {
    const nextHostelId = event.target.value;

    setSelectedHostelId(nextHostelId);

    if (!nextHostelId) {
      setGanttData([]);
      setRoomOverrides({});
      setSelectedTenant(null);
      setErrorMsg("");
    }
  }}
>
  <MenuItem value="">Select property</MenuItem>

  {hostelList.map((hostel) => (
    <MenuItem key={hostel.id} value={hostel.id}>
      {hostel.hostelName}
    </MenuItem>
  ))}
</Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md="auto">
              <Button fullWidth={isMobile} variant="outlined" startIcon={<RestartAltIcon />}
               onClick={() =>
  selectedHostelId &&
  loadOccupancy(selectedHostelId, minDate, maxDate)
}
                disabled={loading || !selectedHostelId}
                sx={{ borderRadius: 2, textTransform: "none", minHeight: 38 }}>
                Refresh data
              </Button>
            </Grid>
            <Grid item xs={6} md="auto">
              <Typography sx={{ fontSize: 11.5, color: "#64748b", px: 0.5 }}>
                {selectedHostelId ? "Auto-loaded · choose a timeline view below" : "Select a property to load daily operations"}
              </Typography>
            </Grid>
            <Grid item xs={12} md="auto">
              <Tooltip title="Fullscreen chart">
                <IconButton size="small" onClick={handleFullscreen}><FullscreenIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>

        {loading && <Box display="flex" justifyContent="center" my={4}><CircularProgress /></Box>}
        {errorMsg && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMsg("")}>{errorMsg}</Alert>}

        {ganttData.length > 0 && <Grow in={!loading}>
          <Grid container spacing={1.25} mb={1.5}>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard icon={<PeopleAltIcon />} title="Occupants in View" value={visibleTenants.length} color="#2563eb" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard icon={<RestaurantIcon />} title="Food Only" value={foodCount} color="#f59e0b" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard icon={<CheckCircleIcon />} title="Active Today" value={activeCount} color="#16a34a" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard icon={<ScheduleIcon />} title="Upcoming" value={upcomingCount} color="#7c3aed" />
            </Grid>
          </Grid>
        </Grow>}

        {!loading && (
          <Fade in>
            <Paper sx={{ p: { xs: 1, sm: 1.5 }, borderRadius: 2.5, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(15,23,42,.05)" }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
                spacing={2}
                mb={1.25}
              >
                <Stack
  direction="row"
  alignItems="center"
  justifyContent="space-between"
  spacing={0.5}
  sx={{
    width: "100%",
    minWidth: 0,
    flex: 1,
  }}
>
  <Tooltip title="Previous period">
    <span>
      <IconButton
        onClick={() => navigateRange(-1)}
        size="small"
        disabled={!ganttData.length}
        sx={{ flexShrink: 0, width: 36, height: 36 }}
      >
        <ChevronLeftIcon />
      </IconButton>
    </span>
  </Tooltip>

  <Typography
    fontWeight={800}
    sx={{
      flex: 1,
      minWidth: 0,
      textAlign: "center",
      fontSize: { xs: 12, sm: 14 },
      lineHeight: 1.4,
      whiteSpace: "normal",
      overflowWrap: "break-word",
      px: 0.5,
    }}
  >
    {formatRange(view, minDate, maxDate)}
  </Typography>

  <Tooltip title="Next period">
    <span>
      <IconButton
        onClick={() => navigateRange(1)}
        size="small"
        disabled={!ganttData.length}
        sx={{ flexShrink: 0, width: 36, height: 36 }}
      >
        <ChevronRightIcon />
      </IconButton>
    </span>
  </Tooltip>
</Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {[
                    { value: "daily", label: "Daily" },
                    { value: "weekly", label: "Weekly" },
                    { value: "monthly", label: "Monthly" },
                  ].map((option) => (
                    <Button key={option.value} size="small"
                      variant={view === option.value ? "contained" : "outlined"}
                      disabled={!ganttData.length}
                      onClick={() => setView(option.value)}
                      sx={{ textTransform: "none", borderRadius: 2 }}>
                      {option.label}
                    </Button>
                  ))}
                </Stack>
              </Stack>

              <Stack
                direction="row"
                spacing={1.5}
                mb={2}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
                sx={{ p: 0.9, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #eef2f7" }}
              >
                <Typography sx={{ fontSize: 11.5, fontWeight: 750, color: "#334155" }}>Legend</Typography>
                <Typography sx={{ fontSize: 11.5, color: "#475569" }}>🔵 Active</Typography>
                <Typography sx={{ fontSize: 11.5, color: "#475569" }}>🟢 Upcoming</Typography>
                <Typography sx={{ fontSize: 11.5, color: "#475569" }}>⚪ Completed</Typography>
                <Typography sx={{ fontSize: 11.5, color: "#475569" }}>🟡 Food only</Typography>
                <Typography sx={{ fontSize: 11, color: "#64748b" }}>Fee icon: paid / pending / unknown · Right-click a stay booking for actions</Typography>
              </Stack>

              {visibleTenants.length > 0 ? (
                <Box ref={chartContainerRef} sx={{
                  overflow: "hidden", borderRadius: 2,
                  background: "#fff", border: "1px solid #e2e8f0", p: 0,
                }}>
                  <Box sx={{ minWidth: 0, width: "100%" }}>
                    {isMobile && (
                      <Typography sx={{ textAlign: "center", mb: 0.5, fontSize: 11, color: "text.secondary" }}>
                        Swipe horizontally to view the timeline.
                      </Typography>
                    )}
     <GanttChart
  tenantsOnly={visibleTenants}
  roomOptionsSource={flattened}
  hostelId={selectedHostelId}
  hostelList={hostelList}
  onTenantAction={(action, tenant) => {
  if (action === "edit") {
    setSelectedHosteler({
      ...tenant,
      hostelerId: tenant.hostelerId,
      hostelId: tenant.hostelId ?? selectedHostelId,
    });

    setIsUpdate(true);
    setIsDialogOpen(true);
    return;
  }

  if (action === "view") {
    setSelectedTenant(tenant);
  }
}}
  chartHeight={chartHeight}
  minDate={minDate}
  maxDate={maxDate}
  tickUnit={tickUnit}
  view={view}
  isMobile={isMobile}
  setSelectedTenant={setSelectedTenant}
  onMoveTenant={async () => {
  await loadOccupancy(selectedHostelId, minDate, maxDate);
}}
/>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: { xs: 5, md: 8 } }}>
                  <Typography fontSize={36}>{ganttData.length ? "📅" : "🏨"}</Typography>
                  <Typography variant="subtitle1" fontWeight={750}>
                    {ganttData.length ? "No Occupancy Data in This Period" : "View Your Property Occupancy"}
                  </Typography>
                  <Typography color="text.secondary" mt={1}>
                    {ganttData.length
                      ? "Try Previous or Next to inspect another period within the loaded occupancy data."
                      : "Select a property above. Its occupancy timeline will load automatically."}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Fade>
        )}

        <TenantDialog selectedTenant={selectedTenant} setSelectedTenant={setSelectedTenant} />
		
		
{/* Create/Edit Hosteler Dialog */}
<Dialog
  open={isDialogOpen}
  onClose={(event, reason) => {
    if (
      reason === "backdropClick" ||
      reason === "escapeKeyDown"
    ) {
      return;
    }

    handleCloseDialog();
  }}
  disableEscapeKeyDown
  fullWidth
  maxWidth="sm"
>
  <DialogTitle>
    {isUpdate ? "Edit Hosteler" : "Create Hosteler"}
  </DialogTitle>

  <DialogContent>
    {isDialogOpen && selectedHosteler && (
  <CreateHosteler
    key={`${isUpdate}-${selectedHosteler.id ?? ""}-${selectedHosteler.hostelId ?? ""}`}
    hosteler={selectedHosteler}
    onClose={handleCloseDialog}
  />
)}
  </DialogContent>
</Dialog>

      </Box>
    </>
  );
};

export default HostelerGantt;
